import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "axomind_chat_session_v1";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const SUGGESTIONS = [
  "What services does Axomind offer?",
  "How do I start an SIP?",
  "Explain ELSS for tax saving",
  "What is a goal-based investment plan?",
];

const WELCOME =
  "Hi! I'm **AxoAssist**, the AI concierge for Axomind Asset Services. Ask me anything about mutual funds, SIPs, retirement planning, ELSS, or our services. How can I help?";

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scroller = useRef(null);
  const sessionId = useRef(null);

  useEffect(() => {
    sessionId.current = getSessionId();
  }, []);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ session_id: sessionId.current, message: content }),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Server returned ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.replace(/^data:\s*/, "");
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + obj.delta,
                };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content:
                    "I'm having trouble connecting right now. Please try again in a moment, or call us at +91 84120 33559 for immediate assistance.",
                };
                return copy;
              });
            }
            if (obj.done) {
              // done
            }
          } catch (_e) {
            // ignore malformed line
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content:
            "Sorry — I'm temporarily unavailable. Please call +91 84120 33559 or use the Contact form.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(true)}
        aria-label="Open AI assistant"
        data-testid="chatbot-trigger"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, type: "spring", stiffness: 200 }}
        className={`fixed bottom-6 right-[5.5rem] z-40 inline-flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-gradient-to-r from-[#1A1407] to-[#0F0B05] border border-[#D4AF37]/40 text-white shadow-[0_15px_45px_-10px_rgba(212,175,55,0.45)] hover:border-[#D4AF37] transition ${
          open ? "hidden" : "flex"
        }`}
      >
        <span className="relative flex w-7 h-7 rounded-full bg-[#D4AF37] items-center justify-center text-black">
          <Bot size={16} />
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-[#1A1407]" />
        </span>
        <span className="text-sm font-medium hidden sm:inline">Ask AxoAssist</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            role="dialog"
            aria-label="AI assistant"
            className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[400px] z-[60] bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden h-[70vh] sm:h-[600px] max-h-[80vh]"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-[#1A1407] to-[#0F0B05]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center text-black">
                  <Bot size={18} />
                </div>
                <div>
                  <div className="font-display font-semibold text-white">AxoAssist</div>
                  <div className="text-xs text-emerald-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="chatbot-close"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white flex items-center justify-center transition"
              >
                <X size={16} />
              </button>
            </div>

            <div
              ref={scroller}
              className="flex-1 overflow-y-auto p-4 space-y-3"
              data-testid="chatbot-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#D4AF37] text-black rounded-br-md"
                        : "bg-white/5 border border-white/10 text-gray-100 rounded-bl-md"
                    }`}
                    data-testid={`chat-msg-${m.role}-${i}`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse [animation-delay:.15s]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse [animation-delay:.3s]" />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="pt-2">
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-2">Suggestions</div>
                  <div className="grid gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`chat-suggestion-${s.slice(0, 10)}`}
                        className="text-left text-sm text-gray-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3 py-2 transition"
                      >
                        <Sparkles size={12} className="inline mr-1.5 text-[#D4AF37]" />
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3 flex items-center gap-2 bg-black/40"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={streaming}
                placeholder={streaming ? "AxoAssist is typing…" : "Ask about SIP, ELSS, retirement…"}
                data-testid="chatbot-input"
                aria-label="Type your message"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:border-[#D4AF37]/60 focus:outline-none disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chatbot-send"
                aria-label="Send message"
                className="w-10 h-10 rounded-full bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#FFD700] disabled:opacity-50 transition"
              >
                <Send size={16} />
              </button>
            </form>

            <div className="px-4 py-2 text-[10px] text-gray-600 text-center border-t border-white/5">
              AxoAssist provides general information only. Mutual fund investments are subject to market risks.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
